import React, { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { RoutePaths } from 'routes/paths'
import PaperWrapper from 'components/custom/PaperWrapper'
import ContentBodyWrapper from 'components/custom/ContentBodyWrapper'
import { componentPermissions } from 'config/permissions'
import { componentNames } from 'config/roles'
import { ReactComponent as ToolboxIcon } from 'assets/icons/017-arrows.svg'
import { ReactComponent as MatcherIcon } from 'assets/icons/032-searching-data-in-database.svg'
import { ReactComponent as InterpreterIcon } from 'assets/icons/041-upload.svg'
import { ReactComponent as MpnIcon } from 'assets/icons/014-chip.svg'

import { Link } from 'react-router-dom'

const Toolbox: FC = () => {
  const { t } = useTranslation()
  const tools = [
    {
      key: 'matcher',
      href: '/toolbox/matcher',
      icon: <MatcherIcon className="w-8 h-8 text-primary-600" />,
      title: 'Matcher',
      subtitle: 'AI-Powered Entity Mapping',
      blurb:
        'Automatically map custom BoM names to the correct entries in the PCF database using AI.',
    },
    {
      key: 'assembler',
      href: '/toolbox/assembler',
      icon: <ToolboxIcon className="w-8 h-8 text-primary-600" />,
      title: 'Assembler',
      subtitle: 'Build Product Carbon Footprints',
      blurb:
        'Create ISO 14067-compliant PCFs fast—no deep LCA expertise or complex software needed.',
    },
    {
      key: 'interpreter',
      href: '/toolbox/interpreter',
      icon: <InterpreterIcon className="w-8 h-8 text-primary-600" />,
      title: 'Interpreter',
      subtitle: 'From BoM to Carbon Insight',
      blurb:
        'Paste or upload a BoM and instantly get matched materials with CO₂ values—one-click to Assembler.',
    },
    {
      key: 'mpn-search',
      href: '/toolbox/mpn-search',
      icon: <MpnIcon className="w-8 h-8 text-primary-600" />,
      title: 'MPN Search',
      subtitle: 'Carbon Data for Electronics',
      blurb:
        'Enter a Manufacturer Part Number to retrieve emission factors (kgCO₂/kg and kgCO₂/pcs).',
    },
  ]

  return (
    <ContentBodyWrapper title={t('toolbox.title')}>
      <PaperWrapper>
        {/* Header / status */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold">Toolbox Overview</h1>
            <p className="text-sm text-gray-600 mt-1">
              The sustamize Toolbox is a powerful set of tools to enable the
              generation of Product Carbon Footprints (PCFs) in a simple,
              accurate and effective way. From automated matching of Bills of
              Materials to building assemblies and generating PCF reports.
            </p>
          </div>
          <span className="inline-flex items-center rounded-full bg-yellow-100 text-yellow-800 px-3 py-1 text-xs font-semibold whitespace-nowrap">
            Alpha / Limited Access
          </span>
        </div>

        {/* Full-width grid of tiles */}
        <div className="row w-full flex flex-wrap px-4 pb-3">
          {tools.map((tool) => (
            <div
              key={tool.key}
              className="
        px-4 pb-6
        w-full md:w-1/2
        flex
      "
            >
              <Link
                to={tool.href}
                aria-label={`${tool.title} – ${tool.subtitle}`}
                className="group flex-1 block focus:outline-none"
              >
                <article
                  className="
            h-full w-full
            rounded-2xl border border-gray-200 bg-white
            p-8
            shadow-md
            transition duration-200 ease-out
            hover:-translate-y-1 hover:scale-[1.03] hover:shadow-xl
            focus-visible:-translate-y-1 focus-visible:scale-[1.03] focus-visible:shadow-xl
            flex flex-col
          "
                >
                  <div className="flex items-start gap-4">
                    <div className="shrink-0">{tool.icon}</div>
                    <div>
                      <h2 className="text-xl font-semibold leading-tight">
                        {tool.title}
                      </h2>
                      <p className="text-base text-gray-600">{tool.subtitle}</p>
                    </div>
                  </div>

                  <p className="mt-4 text-base text-gray-700 flex-1">
                    {tool.blurb}
                  </p>

                  <div className="mt-6 inline-flex items-center gap-2 text-base font-medium text-primary-700">
                    <span className="transition-transform group-hover:translate-x-0.5">
                      Open
                    </span>
                    <svg
                      className="h-5 w-5 transition-transform group-hover:translate-x-0.5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10.293 3.293a1 1 0 011.414 0l5 5a1 1 0 01-1.414 1.414L12 6.414V16a1 1 0 11-2 0V6.414L6.707 9.707A1 1 0 015.293 8.293l5-5z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                </article>
              </Link>
            </div>
          ))}
        </div>
      </PaperWrapper>
    </ContentBodyWrapper>
  )
}

const exportObj = {
  routeProps: {
    path: RoutePaths.toolbox,
    title: 'toolbox.title',
    element: <Toolbox />,
    roles: componentPermissions[componentNames.pageToolbox],
    requiresAuthentication: true,
  },
  name: 'Toolbox',
}

export default exportObj
